import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LifeBuoy, Sparkles, BookOpen, HelpCircle, X as XIcon, Lock } from "lucide-react";
import MentorDrawer from "./MentorDrawer";
import HelpDrawer from "./HelpDrawer";
import { GlossarySheet } from "./GlossaryLauncher";
import { useAuth } from "../context/AuthContext";
import { useSubscription, FEATURES } from "../lib/subscription";
import { trackEvent } from "../lib/telemetry";

/**
 * Feb 26 2026 · AssistanceDock — a single collapsible floating icon
 * that replaces the three separate MentorLauncher / GlossaryLauncher
 * / HelpLauncher pills. Users tap the pill to expand a vertical fan
 * of three options: Ask Mentor · Glossary · Help. Each option
 * mounts the SAME drawer the legacy launcher used, so no behaviour
 * regressions.
 *
 * Screen-space wins:
 *   • Collapsed footprint is a single 48×48 pill (vs. 3 pills before).
 *   • The three legacy launchers stay in the codebase (unit tests +
 *     positioning tests still pass) but are no longer mounted from
 *     App.js.
 *
 * Behaviour parity:
 *   • Same `tlb:ask-mentor` event bus for Explain-Section handoffs.
 *   • Same lock/trial gating from `useSubscription()`.
 *   • Same age-range guard from `user.personality_profile` — no
 *     dock renders until a user has finished the profile step.
 */
export default function AssistanceDock() {
  const { user } = useAuth();
  const { lock, loading } = useSubscription();
  const [expanded, setExpanded] = useState(false);
  const [mentorOpen, setMentorOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [pendingMentorPrompt, setPendingMentorPrompt] = useState(null);
  // Feb 25 2027 · Section-scoped anchor. Populated by both
  // `tlb:ask-mentor` (fresh generation) and `tlb:open-mentor`
  // (revisit) with a section name. When set, MentorDrawer scrolls
  // to and briefly highlights the corresponding mentor reply once
  // the active thread loads.
  const [focusSectionName, setFocusSectionName] = useState(null);
  const dockRef = useRef(null);

  // Explain-Section handoff — same custom-event bus MentorLauncher
  // used, kept verbatim so downstream callers don't need to change.
  // Feb 25 2027 · Also listen for `tlb:open-mentor` — the follow-up
  // event fired when the founder taps an already-explained chip and
  // wants to re-open the drawer to read the previous explanation.
  useEffect(() => {
    const onAsk = (e) => {
      const detail = e?.detail;
      // Support both the old (string) shape and the new
      // {prompt, sectionName} shape so a stale caller isn't broken.
      if (typeof detail === "string") {
        setPendingMentorPrompt(detail);
      } else if (detail && typeof detail === "object") {
        if (detail.prompt) setPendingMentorPrompt(detail.prompt);
        if (detail.sectionName) setFocusSectionName(detail.sectionName);
      }
      setMentorOpen(true);
      setExpanded(false);
    };
    const onOpen = (e) => {
      const detail = e?.detail;
      if (detail && typeof detail === "object" && detail.sectionName) {
        setFocusSectionName(detail.sectionName);
      }
      setMentorOpen(true);
      setExpanded(false);
    };
    window.addEventListener("tlb:ask-mentor", onAsk);
    window.addEventListener("tlb:open-mentor", onOpen);
    return () => {
      window.removeEventListener("tlb:ask-mentor", onAsk);
      window.removeEventListener("tlb:open-mentor", onOpen);
    };
  }, []);

  // Auto-collapse when clicking outside the dock while it's open.
  useEffect(() => {
    if (!expanded) return undefined;
    const onDown = (e) => {
      if (dockRef.current && !dockRef.current.contains(e.target)) {
        setExpanded(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [expanded]);

  // Escape key closes the fan when nothing else is open.
  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setExpanded(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);

  if (!user) return null;
  if (!user.personality_profile?.age_range && !user.personality_profile?.skipped_at) return null;
  if (loading) return null;

  const { allowed: mentorAllowed, locked: mentorLocked, isTrial: mentorTrial } = lock(FEATURES.MENTOR);

  // Basic tier without any mentor entitlement AND not on trial:
  // still show the dock so Glossary + Help work — the mentor option
  // renders as a "subscribe" pill.
  const showMentorSubscribe = mentorLocked && mentorTrial;
  const showMentorAsk = mentorAllowed;
  const hideMentorEntirely = mentorLocked && !mentorTrial;

  return (
    <div
      ref={dockRef}
      className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-2"
      style={{ bottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}
      data-testid="assistance-dock"
      data-expanded={expanded ? "true" : "false"}
    >
      {expanded && (
        <div
          className="flex flex-col items-end gap-2"
          data-testid="assistance-dock-fan"
          role="menu"
          aria-label="Assistance options"
        >
          {!hideMentorEntirely && (
            showMentorSubscribe ? (
              <Link
                to="/billing"
                data-testid="assistance-dock-mentor-trial-cta"
                aria-label="Subscribe to unlock Founder Mentor"
                role="menuitem"
                onClick={() => {
                  setExpanded(false);
                  trackEvent("trial_cta_clicked", {
                    source: "assistance_dock",
                    feature: "mentor",
                    destination: "billing",
                  });
                }}
                className="group flex items-center h-11 pl-3 pr-1 bg-white hover:bg-[#7C5FB0] hover:text-white text-neutral-900 border-2 border-dashed border-neutral-900 shadow-[4px_4px_0_0_rgba(0,0,0,0.18)] transition-colors rounded-none"
              >
                <span className="text-[11px] uppercase tracking-[0.2em] font-black mr-2">Mentor · subscribe</span>
                <span className="w-7 h-7 flex items-center justify-center bg-neutral-300 group-hover:bg-white text-neutral-700 group-hover:text-[#7C5FB0]">
                  <Lock className="w-4 h-4" />
                </span>
              </Link>
            ) : showMentorAsk ? (
              <button
                type="button"
                data-testid="assistance-dock-mentor-btn"
                aria-label="Ask Founder Mentor"
                role="menuitem"
                onClick={() => { setMentorOpen(true); setExpanded(false); }}
                className="group flex items-center h-11 pl-3 pr-1 bg-neutral-900 hover:bg-[#7C5FB0] text-white shadow-[4px_4px_0_0_rgba(0,0,0,0.18)] border-2 border-neutral-900 hover:border-[#7C5FB0] transition-colors rounded-none"
              >
                <span className="text-[11px] uppercase tracking-[0.2em] font-black mr-2">Ask mentor</span>
                <span className="w-7 h-7 flex items-center justify-center bg-[#7C5FB0] group-hover:bg-white group-hover:text-[#7C5FB0]">
                  <Sparkles className="w-4 h-4" />
                </span>
              </button>
            ) : null
          )}
          <button
            type="button"
            data-testid="assistance-dock-glossary-btn"
            aria-label="Open glossary"
            role="menuitem"
            onClick={() => { setGlossaryOpen(true); setExpanded(false); }}
            className="group flex items-center h-11 pl-3 pr-1 bg-white hover:bg-neutral-900 hover:text-white text-neutral-900 border-2 border-neutral-900 shadow-[4px_4px_0_0_rgba(0,0,0,0.18)] transition-colors rounded-none"
          >
            <span className="text-[11px] uppercase tracking-[0.2em] font-black mr-2">Glossary</span>
            <span className="w-7 h-7 flex items-center justify-center bg-[#2F8F6B] text-white flex-shrink-0">
              <BookOpen className="w-4 h-4" />
            </span>
          </button>
          <button
            type="button"
            data-testid="assistance-dock-help-btn"
            aria-label="Open help"
            role="menuitem"
            onClick={() => { setHelpOpen(true); setExpanded(false); }}
            className="group flex items-center h-11 pl-3 pr-1 bg-white hover:bg-neutral-900 hover:text-white text-neutral-900 border-2 border-neutral-900 shadow-[4px_4px_0_0_rgba(0,0,0,0.18)] transition-colors rounded-none"
          >
            <span className="text-[11px] uppercase tracking-[0.2em] font-black mr-2">Help</span>
            <span className="w-7 h-7 flex items-center justify-center bg-[#4A4458] text-white flex-shrink-0">
              <HelpCircle className="w-4 h-4" />
            </span>
          </button>
        </div>
      )}

      <button
        type="button"
        data-testid="assistance-dock-toggle"
        aria-label={expanded ? "Close assistance menu" : "Open assistance menu"}
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        className={`group flex items-center justify-center w-12 h-12 border-2 rounded-none transition-colors shadow-[6px_6px_0_0_rgba(0,0,0,0.18)] hover:shadow-[8px_8px_0_0_rgba(0,0,0,0.24)] ${
          expanded
            ? "bg-neutral-900 text-white border-neutral-900 hover:bg-neutral-800"
            : "bg-white text-neutral-900 border-neutral-900 hover:bg-neutral-900 hover:text-white"
        }`}
      >
        {expanded ? (
          <XIcon className="w-5 h-5" />
        ) : (
          <LifeBuoy className="w-5 h-5" />
        )}
      </button>

      {/* Drawers — mounted once, reused across every fan tap. */}
      <MentorDrawer
        open={mentorOpen}
        onOpenChange={setMentorOpen}
        pendingPrompt={pendingMentorPrompt}
        onPromptConsumed={() => setPendingMentorPrompt(null)}
        focusSectionName={focusSectionName}
        onFocusConsumed={() => setFocusSectionName(null)}
      />
      <HelpDrawer open={helpOpen} onOpenChange={setHelpOpen} />
      <GlossarySheet open={glossaryOpen} onOpenChange={setGlossaryOpen} />
    </div>
  );
}
